/**
 * The phase in view, edited in place: its name, its kind, its length, and the two acts that change
 * how many phases the plan has.
 *
 * ── It has no trigger of its own any more: the SELECTED TAB opens it ────────────────────────
 * The 07.09 programator mock draws chips, "+ Faza" and a summary in the phase band, and nothing
 * else — so the ⋯ that used to stand beside the strip is gone and pressing the tab the canvas is
 * already showing is what opens this panel. A press on any OTHER tab still just selects it, which
 * keeps one gesture doing one thing per tab.
 *
 * The tablist is untouched by that, and it had to be: a `tablist` may own only tabs, and a menu
 * button inside each one made the band announce as "tab list, six items" holding three tabs (axe:
 * `aria-required-children`). So this component keeps no `Popover.Trigger` at all — `PhaseBar` holds
 * the open flag and hands over the ACTIVE TAB as the anchor and as the focus return. The selected
 * tab carries `aria-haspopup="dialog"` and `aria-expanded`, which is how the second press is
 * announced without touching the tab's name or its role.
 *
 * The editor still always acts on the phase the canvas is showing — that has not changed, it is now
 * structurally true rather than a convention, because the only tab that opens it is that one.
 *
 * ── Base UI supplies the behaviour, this file supplies every visual byte ─────────────────────
 * `Popover` rather than `Menu`: a menu's children are `menuitem`s, and this panel holds a text
 * field, a radio pair and a stepper. Rather than `Dialog`, because nothing here is modal — the
 * trainer can see the phase they are editing behind it, and dismissing costs nothing.
 *
 * ── It CLOSES for the delete confirm, and that is a correctness rule ─────────────────────────
 * The open state is held here rather than left to Base UI, for one reason: raising the phase-delete
 * confirm has to close this panel. It does not close itself — a popover is not modal and nothing
 * about a dialog opening elsewhere in the tree dismisses one — so the two surfaces stood on screen
 * together, and with three or more phase tabs the popover overlapped the dialog and stayed LIVE
 * above it. A reviewer pressed „Duplikuj fazę" with „Usunąć fazę?" still unanswered and got a third
 * phase. A `z-index` cannot fix that: the popover would only be under the dialog, still hoverable
 * and still clickable wherever the dialog does not cover it. The confirm has to be the only live
 * surface, so the panel goes.
 *
 * `finalFocus` is stated rather than left to the default for the same reason: this close is
 * PROGRAMMATIC, not a dismissal, so there is no interaction for Base UI to infer a return from, and
 * focus has to be put back on the ⋯ the trainer opened. The confirm then takes it, and hands it back
 * there when it is cancelled. On a CONFIRM the phase is gone and the band is what changed, so
 * `usePhaseEditing` sends focus there instead.
 *
 * ── Every refusal is drawn, never only announced ─────────────────────────────────────────────
 * View ADR §12: a refused control keeps `aria-disabled` and its reason rather than the `disabled`
 * attribute, which drops it out of the tab order and takes the explanation with it. The reason is
 * ordinary on-screen text under the control, because a `title` reaches neither a touch user nor a
 * keyboard user who never hovers — the same correction `+ Faza` and the rest stepper already took.
 */

import { Popover } from '@base-ui/react/popover';
import { useId, useState } from 'react';

import type { PhaseEditorView } from '../model/phaseEditorView';

import styles from './PhaseEditor.module.css';

export interface PhaseEditorActions {
  readonly rename: (name: string) => void;
  readonly retype: (type: 'build' | 'deload') => void;
  readonly resize: (cycles: number) => void;
  /**
   * Copy the phase in view.
   *
   * Answers `'asked'` when the copy would move a block the client is already scheduled on and a
   * confirm was raised instead of the edit being made. The panel then LEAVES, for the reason the
   * header gives about the delete: the confirm has to be the only live surface, and a popover left
   * standing over it is still clickable. `'done'` means the copy happened and the panel stays open
   * on it — the band has already moved to the copy, so closing would be a second thing happening.
   */
  readonly duplicate: (name: string) => 'asked' | 'done';
  /** Raises the confirm. Nothing is written until it is answered. */
  readonly requestDelete: () => void;
}

interface PhaseEditorProps {
  readonly view: PhaseEditorView;
  readonly actions: PhaseEditorActions;
  readonly open: boolean;
  /**
   * Base UI's own signature, not a bare `(open: boolean)`.
   *
   * The band needs the event DETAILS: the tab that opens this panel is outside the popup, so Base UI
   * reads a second press on it as a dismissal and the tab's own handler then re-opens it. `PhaseBar`
   * exempts that one press by reading `details.reason`, and it can only do that if the callback
   * carries them.
   */
  readonly onOpenChange: NonNullable<Popover.Root.Props['onOpenChange']>;
  /**
   * Close the panel WITHOUT a Base UI event behind it — the one close nothing pressed.
   *
   * Raising the delete confirm has to take this panel down first (see the header), and that is a
   * decision rather than a dismissal, so it has no event to describe. Separate from `onOpenChange`
   * because that one exists to be told WHY.
   */
  readonly onClose: () => void;
  /**
   * The tab this panel hangs off — the anchor Base UI positions against AND where focus goes when
   * the panel closes.
   *
   * A FUNCTION rather than a ref, and that is the whole of the wiring: which tab is selected changes
   * without any tab unmounting, so a ref filled by a `ref` callback would go on pointing at whatever
   * was selected when it was last called. Asked at open time it is always right. Base UI accepts a
   * resolver for both slots — `anchor` in
   * `node_modules/@base-ui/react/utils/useAnchorPositioning.d.ts`, `finalFocus` in
   * `node_modules/@base-ui/react/popover/popup/PopoverPopup.d.ts` — so the tab never has to become a
   * `Popover.Trigger`, which it must not, being a `role="tab"` inside a `tablist`.
   */
  readonly anchor: () => HTMLButtonElement | null;
}

export function PhaseEditor({ view, actions, open, onOpenChange, onClose, anchor }: PhaseEditorProps) {
  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Portal>
        <Popover.Positioner
          className={styles.positioner}
          anchor={anchor}
          sideOffset={8}
          align="start"
        >
          <Popover.Popup className={styles.popup} finalFocus={anchor}>
            <Popover.Title className={styles.title}>{view.title}</Popover.Title>
            <PhaseFields view={view} actions={actions} close={onClose} />
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * The panel's contents, split out so the name field's local state is created when the popup MOUNTS
 * rather than living for the life of the band.
 *
 * That is what makes "the editor opens on what was authored" true after every other edit: switching
 * phases, undoing a rename, or a save that re-seeds the tree all replace the popup rather than
 * leaving stale text in a field that was mounted before any of it happened.
 */
function PhaseFields({
  view,
  actions,
  close,
}: {
  readonly view: PhaseEditorView;
  readonly actions: PhaseEditorActions;
  readonly close: () => void;
}) {
  const nameId = useId();
  const typeLabelId = useId();
  const lockedReasonId = useId();
  const shortenReasonId = useId();
  const lengthenReasonId = useId();
  const lengthValueId = useId();
  const groupName = useId();

  const [name, setName] = useState(view.name);
  /**
   * The authored name is the source of truth, and the field follows it.
   *
   * Base UI unmounts the popup when it closes, so the `useState` seed above is right every time the
   * trainer OPENS the editor. What this covers is the document changing while it is open — Cmd+Z on
   * a rename, or a save re-seeding the tree — after which the field would otherwise hold a name that
   * no longer exists and commit it back on blur.
   *
   * Adjusted during RENDER rather than in an effect: React documents this as the way to reset state
   * when a prop changes ("You Might Not Need an Effect"), and `react-hooks/set-state-in-effect`
   * rejects the effect form because it renders twice and can cascade. Typing is untouched —
   * `seenName` only moves when the DOCUMENT's name does.
   */
  const [seenName, setSeenName] = useState(view.name);
  if (seenName !== view.name) {
    setSeenName(view.name);
    setName(view.name);
  }

  const commitName = () => {
    const trimmed = name.trim();
    // A no-op commit records nothing — the operation refuses a blank and ignores an unchanged
    // value, and the field snaps back so the trainer sees what was actually kept.
    if (trimmed.length === 0) {
      setName(view.name);
      return;
    }
    if (trimmed !== view.name) actions.rename(trimmed);
  };

  return (
    <>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={nameId}>
          {view.nameLabel}
        </label>
        <input
          id={nameId}
          className={styles.input}
          type="text"
          value={name}
          maxLength={view.nameMaxLength}
          autoComplete="off"
          onChange={(event) => {
            setName(event.target.value);
          }}
          onBlur={commitName}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              commitName();
              event.currentTarget.blur();
            }
            // Escape belongs to the FIELD only while there is an edit to revert: it puts the
            // authored name back and stops there, rather than closing the popover out from under a
            // trainer who meant to undo one keystroke.
            //
            // With nothing typed it is NOT the field's key, and swallowing it there made the
            // panel undismissable by keyboard — this input takes focus on open, so the very first
            // Escape a trainer presses is the one that never reached Base UI. React 19 attaches at
            // the root container and `stopPropagation` stops the native event there, so the
            // document-level dismiss listener never ran at all.
            if (event.key === 'Escape' && name !== view.name) {
              setName(view.name);
              event.stopPropagation();
            }
          }}
        />
      </div>

      <fieldset className={styles.field}>
        <legend className={styles.label} id={typeLabelId}>
          {view.typeLabel}
        </legend>
        <div className={styles.segments}>
          {view.types.map((option) => (
            <label
              key={option.key}
              className={`${String(styles.segment)} ${
                option.isSelected ? String(styles.segmentOn) : ''
              }`}
            >
              <input
                className={styles.radio}
                type="radio"
                name={groupName}
                value={option.key}
                checked={option.isSelected}
                // `aria-disabled` on a radio keeps it focusable and still lets the browser refuse
                // the change, which is what §12 asks for — the attribute would take the reason with
                // it.
                aria-disabled={view.lockedReason !== null ? 'true' : undefined}
                aria-describedby={view.lockedReason !== null ? lockedReasonId : undefined}
                onChange={() => {
                  if (view.lockedReason === null) actions.retype(option.key);
                }}
              />
              {option.label}
            </label>
          ))}
        </div>
      </fieldset>

      {/* Each end owes its OWN sentence, and they are different sentences: the floor is the client's
        * trained cycles, the ceiling is the plan-wide cap or a finished phase. One shared id put the
        * shrink refusal on the ⊕, so a trainer whose phase could still grow was told "krócej się nie
        * da" under a control that would have worked. */}
      <div className={styles.field}>
        <span className={styles.label} id={lengthValueId}>
          {view.length.label}
        </span>
        <div className={styles.stepper}>
          <StepButton
            step={view.length.decrease}
            glyph="−"
            reasonId={shortenReasonId}
            onPress={actions.resize}
          />
          {/* An `<output>`, whose implicit `role="status"` makes every press announce the new
            * length in words. The same pattern the tape's rest stepper uses, one element over. */}
          <output className={styles.value}>{view.length.value}</output>
          <StepButton
            step={view.length.increase}
            glyph="+"
            reasonId={lengthenReasonId}
            onPress={actions.resize}
          />
        </div>
        {view.length.decrease.disabledReason !== null && (
          <p id={shortenReasonId} className={styles.reason}>
            {view.length.decrease.disabledReason}
          </p>
        )}
        {view.length.increase.disabledReason !== null && (
          <p id={lengthenReasonId} className={styles.reason}>
            {view.length.increase.disabledReason}
          </p>
        )}
      </div>

      <div className={styles.actions}>
        <PhaseAction
          label={view.duplicateLabel}
          reason={view.duplicateRefusal}
          onPress={() => {
            if (actions.duplicate(view.duplicateName) === 'asked') close();
          }}
        />
        <PhaseAction
          label={view.deleteLabel}
          reason={view.deleteRefusal}
          describedBy={view.lockedReason === null ? undefined : lockedReasonId}
          danger
          onPress={() => {
            // The panel goes BEFORE the confirm arrives — see the header. Both land in one commit,
            // so the trainer never sees the two surfaces together.
            close();
            actions.requestDelete();
          }}
        />
        {/* What the client's history freezes, ONCE — the type control above and Delete beside it are
          * refused by the same fact, and printing it twice made the panel stutter. Drawn at the foot
          * rather than beside either control, because it is about the phase rather than about one
          * button, and both point their `aria-describedby` here. */}
        {view.lockedReason !== null && (
          <p id={lockedReasonId} className={styles.reason}>
            {view.lockedReason}
          </p>
        )}
      </div>
    </>
  );
}

function StepButton({
  step,
  glyph,
  reasonId,
  onPress,
}: {
  readonly step: PhaseEditorView['length']['decrease'];
  readonly glyph: string;
  /** Where THIS end's own sentence is drawn. Pointed at only while this end is the refused one. */
  readonly reasonId: string;
  readonly onPress: (cycles: number) => void;
}) {
  return (
    <button
      type="button"
      className={styles.step}
      aria-label={step.label}
      aria-disabled={step.disabledReason !== null}
      aria-describedby={step.disabledReason === null ? undefined : reasonId}
      onClick={() => {
        if (step.disabledReason === null) onPress(step.value);
      }}
    >
      <span aria-hidden="true">{glyph}</span>
    </button>
  );
}

/**
 * Duplicate or Delete, with the reason drawn beside it when it cannot act.
 *
 * `danger` inks the DELETE rather than filling it: `--danger` is a marker colour with no
 * theme-independent ink to sit on, and a filled destructive control in a popover would be the
 * loudest thing on the screen. The confirm that follows is where the weight belongs.
 */
function PhaseAction({
  label,
  reason,
  describedBy,
  danger,
  onPress,
}: {
  readonly label: string;
  /** This control's OWN cause. `null` when only the shared lock applies. */
  readonly reason: string | null;
  /** The panel's shared lock note, when one is in force. Refuses the control on its own. */
  readonly describedBy?: string | undefined;
  readonly danger?: boolean;
  readonly onPress: () => void;
}) {
  const reasonId = useId();
  const refused = reason !== null || describedBy !== undefined;
  return (
    <>
      <button
        type="button"
        className={`${String(styles.action)} ${danger === true ? String(styles.danger) : ''}`}
        aria-disabled={refused ? 'true' : undefined}
        aria-describedby={
          [reason === null ? null : reasonId, describedBy ?? null].filter(Boolean).join(' ') ||
          undefined
        }
        onClick={() => {
          if (!refused) onPress();
        }}
      >
        {label}
      </button>
      {reason !== null && (
        <p id={reasonId} className={styles.reason}>
          {reason}
        </p>
      )}
    </>
  );
}
